"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Bar, ComposedChart, CartesianGrid, Line,
  XAxis, YAxis, Legend, Tooltip, ResponsiveContainer,
} from "recharts";
import { CircleHelp, Download, ChevronDown } from "lucide-react";
import {
  Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import axiosInstance from "@/api/axios";
import { ChartContainer } from "@/components/ui/chart";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Grey palette — bar fill vs. the trend line stroke.
const BAR_COLOR  = "#9CA3AF"; // gray-400
const LINE_COLOR = "#4B5563"; // gray-600

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const CustomTooltip = ({ active, payload, label, mode, barKey, lineKey }) => {
  if (!active || !payload?.length) return null;

  // Two series can be present (Bar + Line) since they use separate data
  // arrays — find the Bar's entry explicitly by dataKey rather than
  // trusting payload[0], which can land on the Line's entry instead.
  const barEntry = payload.find((p) => p.dataKey === barKey);
  if (!barEntry) return null;

  const point = barEntry.payload;
  const value = barEntry.value;
  const avgValue = point[lineKey];

  return (
    <div className="rounded-lg border bg-background p-3 shadow-md text-sm min-w-[170px]">
      <p className="font-semibold mb-2">{label}</p>
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground">Contaminated</span>
        <span className="font-medium">
          {mode === "kg" ? `${Number(value).toFixed(3)} kg` : `${Number(value).toFixed(1)}%`}
        </span>
      </div>
      {avgValue != null && (
        <div className="flex items-center justify-between gap-4 mt-1">
          <span className="text-muted-foreground">Moving average</span>
          <span className="font-medium">
            {mode === "kg" ? `${Number(avgValue).toFixed(3)} kg` : `${Number(avgValue).toFixed(1)}%`}
          </span>
        </div>
      )}
      <div className="border-t mt-2 pt-2 text-xs text-muted-foreground">
        Total items: <span className="font-medium text-foreground">{point.total_contaminated_items ?? 0}</span>
      </div>
    </div>
  );
};

export function ContaminationTrendChart({
  className,
  title,
  bins = [],
  binTypes = {},
  timeRange,
  customDateRange = [{ startDate: null, endDate: null, key: "selection" }],
  isCustomDateRange = false,
  ...props
}) {
  const exportRef = useRef(null);
  const [chartData, setChartData] = useState([]); // real periods only — feeds the bars
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState(null);
  const [mode, setMode]           = useState("kg");

  const chartConfig = {
    contaminated: { label: "Contaminated",   color: BAR_COLOR },
    trend:        { label: "Moving average", color: LINE_COLOR },
  };

  useEffect(() => {
    if (!bins || bins.length === 0) return;
    if (!timeRange && !isCustomDateRange) return;

    setIsLoading(true);
    setError(null);
    setChartData([]);

    const p = new URLSearchParams();
    bins.forEach((b) => p.append("bin", b));
    p.set("period", timeRange || "year");

    if (isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate) {
      const s = customDateRange[0].startDate;
      const e = customDateRange[0].endDate;
      p.set("start_date", s instanceof Date ? toISO(s) : toISO(new Date(s)));
      p.set("end_date",   e instanceof Date ? toISO(e) : toISO(new Date(e)));
    }

    axiosInstance
      .get(`/contamination-timeseries/?${p.toString()}`)
      .then((res) => {
        const data = res.data;
        if (!Array.isArray(data)) { setError("Unexpected response"); return; }

        const shaped = data.map((item) => ({
          period:                    item.period,
          contamination_rate:        item.contamination_rate ?? 0,
          total_contaminated_kg:     item.total_contaminated_kg ?? 0,
          // Contaminated items for THIS period only — API already returns
          // this as `contaminated_items` (see ContaminationTimeSeriesAPIView).
          // NOT the bin's total item count (that's `total_items`).
          total_contaminated_items:  item.contaminated_items ?? 0,
        }));

        // Cumulative (running) average up to and including each point:
        // point i's average = sum of values[0..i] / (i+1) — i.e. all
        // previous points summed, divided by how many there are so far.
        // Point A averages with itself, so the line has a value at the
        // very first bar.
        let kgSum = 0;
        let pctSum = 0;
        const withAvg = shaped.map((d, i) => {
          kgSum  += d.total_contaminated_kg;
          pctSum += d.contamination_rate;
          return {
            ...d,
            mid_kg:  kgSum  / (i + 1),
            mid_pct: pctSum / (i + 1),
          };
        });

        setChartData(withAvg);
      })
      .catch(() => setError("Failed to load data"))
      .finally(() => setIsLoading(false));
  }, [
    timeRange,
    isCustomDateRange,
    JSON.stringify(bins),
    customDateRange?.[0]?.startDate instanceof Date
      ? customDateRange[0].startDate.toDateString()
      : String(customDateRange?.[0]?.startDate ?? ""),
    customDateRange?.[0]?.endDate instanceof Date
      ? customDateRange[0].endDate.toDateString()
      : String(customDateRange?.[0]?.endDate ?? ""),
  ]);

  const barKey = mode === "kg" ? "total_contaminated_kg" : "contamination_rate";
  const lineKey = mode === "kg" ? "mid_kg" : "mid_pct";

  const lineData = useMemo(() => {
    const N = chartData.length;
    if (N === 0) return [];

    return chartData.map((d, i) => ({
      ...d,
      _pos: (i + 0.5) / N,
    }));
  }, [chartData]);

  const yMax = (() => {
    if (!chartData.length) return "auto";

    const max = Math.max(...chartData.map((d) => d[barKey] ?? 0));
    if (mode === "percentage") {
      if (!max) return 100;
      return Math.ceil((max * 1.15) / 5) * 5;
    }
    return max ? Math.ceil(max * 1.15 * 100) / 100 : "auto";
  })();

  const handleExportCSV = () => {
    if (!chartData.length) return;
    const headers = ["Period", "Contamination %", "Contaminated kg", "Moving Average", "Total Items"];
    const rows = chartData.map((d) =>
      [
        d.period,
        d.contamination_rate?.toFixed(2)    ?? "",
        d.total_contaminated_kg?.toFixed(3) ?? "",
        mode === "kg" ? (d.mid_kg?.toFixed(3) ?? "") : (d.mid_pct?.toFixed(2) ?? ""),
        d.total_contaminated_items ?? "",
      ].join(",")
    );
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href     = URL.createObjectURL(blob);
    link.download = `contamination_trend_${timeRange || "data"}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const handleExportPNG = () => {
    if (!exportRef.current) return;
    const controls = exportRef.current.querySelector(".export-controls");
    if (controls) controls.style.display = "none";
    import("html2canvas").then((h) => {
      h.default(exportRef.current, { scale: 2, useCORS: true }).then((canvas) => {
        const link = document.createElement("a");
        link.download = `contamination_trend_${timeRange || "data"}.png`;
        link.href     = canvas.toDataURL("image/png");
        link.click();
      }).finally(() => { if (controls) controls.style.display = ""; });
    });
  };

  return (
    <div
      ref={exportRef}
      className={cn(
        "lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section border-0 shadow-none",
        className
      )}
      {...props}
    >
      <div className="flex flex-wrap justify-between items-start gap-2 pb-2">
        <div>
          <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
            {title || "Contamination Trend"}
          </h4>
          {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {new Date(customDateRange[0].startDate).toLocaleDateString()} –{" "}
              {new Date(customDateRange[0].endDate).toLocaleDateString()}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 export-controls">
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded-lg p-1 border border-gray-200 dark:border-slate-700">
            <button
              onClick={() => setMode("kg")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                mode === "kg"
                  ? "bg-white dark:bg-slate-600 text-primary shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >KG</button>
            <button
              onClick={() => setMode("percentage")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                mode === "percentage"
                  ? "bg-white dark:bg-slate-600 text-primary shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >%</button>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm"
                      className="text-muted-foreground hover:text-primary"
                      disabled={!chartData.length || isLoading}>
                <Download className="h-4 w-4 mr-1" />
                Export
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCSV}>Download as CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPNG}>Download as PNG</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger asChild>
                <CircleHelp className="h-5 w-5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px]">
                Bar height = contamination per period. The dashed line is a running average — each point averages all periods up to and including it — plotted through each bar's midpoint, not its top.
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="px-1 pt-2 sm:px-2 sm:pt-2" style={{ width: "100%", height: "100%" }}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full w-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-destructive">{error}</div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[400px]">
            <div className="text-muted-foreground text-center">
              <p className="mb-2 text-lg">No data available</p>
              <p className="text-sm">Select a time range and bins to view contamination trends</p>
            </div>
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 28, right: 30, left: 0, bottom: 20 }}
                barCategoryGap="20%"
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />

                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 11 }}
                  tickLine={{ stroke: "hsl(var(--border))" }}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  interval="preserveStartEnd"
                  padding={{ left: 0, right: 0 }}
                />

                {/* Hidden numeric axis the Line rides on — independent of
                    the categorical bar axis, spans exactly [0,1] with no
                    padding, so the line's edge anchors sit flush at the
                    true plot corners. */}
                <XAxis
                  xAxisId="lineAxis"
                  dataKey="_pos"
                  type="number"
                  domain={[0, 1]}
                  padding={{ left: 0, right: 0 }}
                  hide
                />

                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={{ stroke: "hsl(var(--border))" }}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  tickFormatter={(v) => mode === "kg" ? `${v} kg` : `${v}%`}
                  domain={[0, yMax]}
                  width={mode === "kg" ? 64 : 44}
                />
                <Tooltip
                  content={<CustomTooltip mode={mode} barKey={barKey} lineKey={lineKey} />}
                  cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }}
                />
                <Legend
                  verticalAlign="bottom"
                  wrapperStyle={{ fontSize: 12, paddingTop: 14 }}
                  payload={[
                    { value: "Contaminated",   type: "square", color: BAR_COLOR },
                    { value: "Moving average", type: "line",   color: LINE_COLOR },
                  ]}
                />

                <Bar
                  dataKey={barKey}
                  fill={BAR_COLOR}
                  name="Contaminated"
                  maxBarSize={60}
                  radius={[2, 2, 0, 0]}
                />

                <Line
                  data={lineData}
                  xAxisId="lineAxis"
                  dataKey={lineKey}
                  type="monotone"
                  stroke={LINE_COLOR}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={{ r: 3, fill: LINE_COLOR, strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                  name="Moving average"
                  isAnimationActive={false}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </div>
    </div>
  );
}

export default ContaminationTrendChart;